// Vendor Billing v1.0 — TanStack Query hooks for the VFI invoice surfaces
// (vendor ledger tab, loan VFI summary card, registry dashboard, drawer
// actions). Mirrors the useRegistries conventions: stable query-key factory
// + mutation invalidation of the whole vendor-invoices subtree.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { vendorInvoicesApi } from "@/api/portfolioAdmin/client";
import type {
  BatchIssueRequest,
  BatchIssueResultDto,
  ContractComparisonDto,
  IssueVendorInvoiceRequest,
  VendorInvoiceDetailDto,
  VendorInvoiceDto,
  VendorInvoiceListParams,
  VfiAgingRowDto,
  VfiDashboardDto,
  VfiPastIncomeRowDto,
  VfiRatingRollupDto,
  VfiScopedSummaryDto,
} from "@/api/portfolioAdmin/types";

export const vendorBillingKeys = {
  all: ["portfolio-admin", "vendor-invoices"] as const,
  list: (params?: VendorInvoiceListParams) =>
    [
      "portfolio-admin",
      "vendor-invoices",
      "list",
      params?.vendorId ?? null,
      params?.loanId ?? null,
      params?.status ?? null,
    ] as const,
  detail: (invoiceId: string) =>
    ["portfolio-admin", "vendor-invoices", "detail", invoiceId] as const,
  dashboard: ["portfolio-admin", "vendor-invoices", "dashboard"] as const,
  // The pre-issue contract check for an invoice that does not exist yet.
  contractCheck: (vendorId: string, amount: number) =>
    ["portfolio-admin", "vendor-invoices", "contract-check", vendorId, amount] as const,
  // The server-computed rollup for one vendor or one loan.
  scopedSummary: (vendorId?: string, loanId?: string) =>
    [
      "portfolio-admin",
      "vendor-invoices",
      "scoped-summary",
      vendorId ?? null,
      loanId ?? null,
    ] as const,
  // Vendor Program v2 PR 3 — VFI Accounting formal outputs. Nested under
  // the vendor-invoices root so issue/void invalidation refreshes them.
  accounting: (
    report: "past-income" | "receivables-aging" | "in-transit" | "outstanding" | "rating-rollup",
  ) =>
    ["portfolio-admin", "vendor-invoices", "accounting", report] as const,
};

export function useVendorInvoices(
  params?: VendorInvoiceListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<VendorInvoiceDto[]>({
    queryKey: vendorBillingKeys.list(params),
    queryFn: () => vendorInvoicesApi.list(params),
    enabled: options?.enabled ?? true,
  });
}

/**
 * The contract check for an invoice that does not exist yet, so the manual
 * issue form can show the comparison and collect an acknowledgement before
 * submitting rather than after a 400. Server-gated on VendorBilling.Issue.
 */
export function useContractCheckPreview(
  vendorId: string | undefined,
  amount: number | undefined,
) {
  const enabled = !!vendorId && Number.isFinite(amount) && (amount ?? 0) > 0;
  return useQuery<ContractComparisonDto>({
    queryKey: vendorBillingKeys.contractCheck(vendorId ?? "", amount ?? 0),
    queryFn: () => vendorInvoicesApi.contractCheck(vendorId!, amount!),
    enabled,
  });
}

/**
 * The VFI rollup for one vendor or one loan, computed SERVER-side.
 *
 * The vendor ledger tab and the loan card each used to derive their own
 * figures from the invoice list, and disagreed with the registry dashboard and
 * with each other — on whether `processing` counts as open, and on which
 * 1 January starts the Paid-YTD year. Deriving them in two places was the bug;
 * one server computation is the fix.
 */
export function useVfiScopedSummary(
  params: { vendorId?: string; loanId?: string },
  options?: { enabled?: boolean },
) {
  const { vendorId, loanId } = params;
  return useQuery<VfiScopedSummaryDto>({
    queryKey: vendorBillingKeys.scopedSummary(vendorId, loanId),
    queryFn: () => vendorInvoicesApi.scopedSummary({ vendorId, loanId }),
    enabled: (options?.enabled ?? true) && (!!vendorId || !!loanId),
  });
}

export function useVendorInvoice(invoiceId: string | undefined) {
  return useQuery<VendorInvoiceDetailDto>({
    queryKey: vendorBillingKeys.detail(invoiceId ?? ""),
    queryFn: () => vendorInvoicesApi.get(invoiceId!),
    enabled: !!invoiceId,
  });
}

export function useVfiDashboard(options?: { enabled?: boolean }) {
  return useQuery<VfiDashboardDto>({
    queryKey: vendorBillingKeys.dashboard,
    queryFn: () => vendorInvoicesApi.dashboard(),
    enabled: options?.enabled ?? true,
  });
}

function useInvalidateVendorInvoices() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: vendorBillingKeys.all });
}

export function useIssueVendorInvoice() {
  const invalidate = useInvalidateVendorInvoices();
  return useMutation({
    mutationFn: (body: IssueVendorInvoiceRequest) => vendorInvoicesApi.issue(body),
    onSuccess: invalidate,
  });
}

// ── Vendor Program v2 PR 3 — VFI Accounting ──────────────────────────────

/** Draft → Issued for one draft invoice. SP-4 WS-2: a contract-variance
 *  draft requires `varianceAckReason` (the server 400s without it). */
export function useIssueDraftInvoice() {
  const invalidate = useInvalidateVendorInvoices();
  return useMutation({
    mutationFn: (args: { invoiceId: string; varianceAckReason?: string | null }) =>
      vendorInvoicesApi.issueDraft(args.invoiceId, args.varianceAckReason),
    onSuccess: invalidate,
  });
}

/** Batch send — per-item isolation; the result lists issued + failed. */
export function useBatchIssueInvoices() {
  const invalidate = useInvalidateVendorInvoices();
  return useMutation<BatchIssueResultDto, unknown, BatchIssueRequest>({
    mutationFn: (body) => vendorInvoicesApi.batchIssue(body),
    onSuccess: invalidate,
  });
}

export function useVfiPastIncome(options?: { enabled?: boolean }) {
  return useQuery<VfiPastIncomeRowDto[]>({
    queryKey: vendorBillingKeys.accounting("past-income"),
    queryFn: () => vendorInvoicesApi.pastIncome(),
    enabled: options?.enabled ?? true,
  });
}

export function useVfiReceivablesAging(options?: { enabled?: boolean }) {
  return useQuery<VfiAgingRowDto[]>({
    queryKey: vendorBillingKeys.accounting("receivables-aging"),
    queryFn: () => vendorInvoicesApi.receivablesAging(),
    enabled: options?.enabled ?? true,
  });
}

/** Money on its way — settling Stripe/ACH payments, carved out of the aging
 *  report so "open receivables" means what it says. */
export function useVfiInTransit(options?: { enabled?: boolean }) {
  return useQuery<VfiAgingRowDto[]>({
    queryKey: vendorBillingKeys.accounting("in-transit"),
    queryFn: () => vendorInvoicesApi.inTransit(),
    enabled: options?.enabled ?? true,
  });
}

export function useVfiOutstanding(options?: { enabled?: boolean }) {
  return useQuery<VfiAgingRowDto[]>({
    queryKey: vendorBillingKeys.accounting("outstanding"),
    queryFn: () => vendorInvoicesApi.outstanding(),
    enabled: options?.enabled ?? true,
  });
}

export function useVfiRatingRollup(options?: { enabled?: boolean }) {
  return useQuery<VfiRatingRollupDto>({
    queryKey: vendorBillingKeys.accounting("rating-rollup"),
    queryFn: () => vendorInvoicesApi.ratingRollup(),
    enabled: options?.enabled ?? true,
  });
}

export function useVoidVendorInvoice(invoiceId: string) {
  const invalidate = useInvalidateVendorInvoices();
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      vendorInvoicesApi.void(invoiceId, body),
    onSuccess: invalidate,
  });
}

export function useRecordManualPayment(invoiceId: string) {
  const invalidate = useInvalidateVendorInvoices();
  return useMutation({
    mutationFn: (body: { receivedAt?: string | null; note?: string | null }) =>
      vendorInvoicesApi.recordManualPayment(invoiceId, body),
    onSuccess: invalidate,
  });
}

export function useCorrectInvoiceStatus(invoiceId: string) {
  const invalidate = useInvalidateVendorInvoices();
  return useMutation({
    mutationFn: (body: { toStatus: string; reason: string }) =>
      vendorInvoicesApi.correctStatus(invoiceId, body),
    onSuccess: invalidate,
  });
}

export function useRefreshInvoicePayment(invoiceId: string) {
  const invalidate = useInvalidateVendorInvoices();
  return useMutation({
    mutationFn: () => vendorInvoicesApi.refreshPayment(invoiceId),
    onSuccess: invalidate,
  });
}
